import { OVERVIEW_TABS } from 'cpool-constants';

import { LEAD_DETAILS_OVERLAYS } from '../constants/overlays';
import { OVERVIEW_TAB_PATHS } from '../constants/tabPaths';
import {
    LEAD_DETAILS_OVERLAY_CONFIG,
    LEAD_DETAILS_OVERLAY_KEYS,
    type LeadDetailsOverlayKey,
    type LeadDetailsOverlaySearch,
    type LeadDetailsOverlayValue,
} from '../schemas/leadDetailsSearch.schema';

const NORMALIZED_SEARCH_KEYS = [...LEAD_DETAILS_OVERLAY_KEYS, 'createApplicantTab'] as const;
const EMPTY_OVERLAY_SEARCH = Object.fromEntries(NORMALIZED_SEARCH_KEYS.map((key) => [key, undefined])) as {
    [Key in (typeof NORMALIZED_SEARCH_KEYS)[number]]: undefined;
};

export function clearLeadDetailsOverlaySearch<Search extends LeadDetailsOverlaySearch>(search: Search): Search {
    return { ...search, ...EMPTY_OVERLAY_SEARCH };
}

export function updateLeadDetailsOverlaySearch<
    Search extends LeadDetailsOverlaySearch,
    Key extends LeadDetailsOverlayKey,
>(search: Search, key: Key, value: LeadDetailsOverlayValue<Key> | undefined) {
    const previousSearch = value === undefined ? search : clearLeadDetailsOverlaySearch(search);
    return { ...previousSearch, [key]: value };
}

function pathnameHasScope(
    pathname: string,
    routeScope: Exclude<(typeof LEAD_DETAILS_OVERLAY_CONFIG)[LeadDetailsOverlayKey]['routeScope'], null>,
): boolean {
    if (routeScope === OVERVIEW_TAB_PATHS[OVERVIEW_TABS.BANK_REQUEST]) {
        return (
            pathname.endsWith(`/${OVERVIEW_TAB_PATHS[OVERVIEW_TABS.BANK_REQUEST]}`) ||
            pathname.endsWith(`/${OVERVIEW_TAB_PATHS[OVERVIEW_TABS.BANK_REQUEST]}/new-request`)
        );
    }

    return pathname.split('/').includes(routeScope);
}

function isActiveOverlay(value: true | string | undefined): boolean {
    return value === true || typeof value === 'string';
}

function isOverlayAvailableOnPath(key: LeadDetailsOverlayKey, pathname: string): boolean {
    const { routeScope } = LEAD_DETAILS_OVERLAY_CONFIG[key];
    return routeScope === null || pathnameHasScope(pathname, routeScope);
}

export function normalizeLeadDetailsSearch<Search extends LeadDetailsOverlaySearch>(
    search: Search,
    pathname: string,
): Search {
    const activeOverlay = LEAD_DETAILS_OVERLAY_KEYS.find(
        (key) => isOverlayAvailableOnPath(key, pathname) && isActiveOverlay(search[key]),
    );
    const normalizedSearch = clearLeadDetailsOverlaySearch(search);
    if (!activeOverlay) return normalizedSearch;

    return {
        ...normalizedSearch,
        [activeOverlay]: search[activeOverlay],
        createApplicantTab:
            activeOverlay === LEAD_DETAILS_OVERLAYS.CREATE_APPLICANT ? search.createApplicantTab : undefined,
    };
}

export function getCanonicalLeadDetailsHref(
    href: string,
    validatedSearch: LeadDetailsOverlaySearch,
    pathname: string,
): string | null {
    const url = new URL(href, 'https://cpool.local');
    const normalizedSearch = normalizeLeadDetailsSearch(validatedSearch, pathname);
    let changed = false;

    NORMALIZED_SEARCH_KEYS.forEach((key) => {
        const value = normalizedSearch[key];
        if (value === undefined) {
            if (url.searchParams.has(key)) {
                url.searchParams.delete(key);
                changed = true;
            }
            return;
        }

        if (url.searchParams.get(key) !== String(value)) {
            url.searchParams.set(key, String(value));
            changed = true;
        }
    });

    return changed ? `${url.pathname}${url.search}${url.hash}` : null;
}
